/**
 * Ticket-template picker — the one reusable `templateVariant` control
 * event-form.view.ts and ticket-type-form.view.ts both mount: a compact
 * horizontal `<schmancy-scroll>` of small, pixel-faithful card thumbnails
 * (`<ticket-template-preview>`, the same tag the gallery's tiles mount, so
 * both surfaces preview the operator's OWN brand and preview it identically),
 * one per TICKET_TEMPLATE_VARIANTS. There is always a template — the `defaultVariant`
 * (the package default for an event, the event's own choice for a ticket
 * type) is pre-selected when nothing is stored, so the control never shows an
 * empty "use the default" placeholder; it shows the default card selected.
 * Picking the default variant clears the stored override back to null
 * (inherit); picking any other sets it. The two forms differ only in the
 * bound value and which `defaultVariant` they inherit from.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { TICKET_TEMPLATE_VARIANTS } from "@momo/ticket-template";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { LocalizeMixin } from "../i18n/localize.ts";
// Side-effect: registers <ticket-template-preview>.
import "./ticket-template-preview.view.ts";

const THUMB_WIDTH = 120;

@schmancyElement("ticket-template-picker")
export class TicketTemplatePicker extends LocalizeMixin(SchmancyElement) {
	/** The stored override — null means "inherit the default" (which is shown
	 *  selected via `defaultVariant`), never an empty placeholder. */
	@property({ attribute: false }) value: TicketTemplateVariant | null = null;
	/** The radiogroup's own accessible name. */
	@property({ type: String }) label = "";
	/** Pre-selected when `value` is null — the package default on an event, the
	 *  event's own template on a ticket type. */
	@property({ attribute: false })
	defaultVariant: TicketTemplateVariant = DEFAULT_TICKET_TEMPLATE_VARIANT;

	private get selected(): TicketTemplateVariant {
		return this.value ?? this.defaultVariant;
	}

	private select(variant: TicketTemplateVariant): void {
		// Picking the default clears the override back to inherit.
		const next = variant === this.defaultVariant ? null : variant;
		if (next === this.value) return;
		this.value = next;
		this.dispatchEvent(
			new CustomEvent<{ value: TicketTemplateVariant | null }>("change", {
				detail: { value: next },
				bubbles: true,
				composed: true,
			}),
		);
	}

	private handleActivateKey(
		e: KeyboardEvent,
		next: TicketTemplateVariant,
	): void {
		if (e.key !== "Enter" && e.key !== " ") return;
		e.preventDefault();
		this.select(next);
	}

	override render(): TemplateResult {
		const selected = this.selected;
		return html`
			<!-- schmancy-scroll reads its own parentElement's computed display to
			     decide between flex-grow sizing and an explicit-height fallback;
			     this wrapper's sole job is being that flex parent so the scroll
			     region sizes to its row of thumbnails instead of collapsing. -->
			<div class="flex">
				<schmancy-scroll direction="horizontal" hide role="radiogroup" aria-label=${this.label}>
					<div class="flex gap-3 p-1">
						${repeat(
							TICKET_TEMPLATE_VARIANTS,
							(variant) => variant,
							(variant) => html`
								<schmancy-surface
									type="transparent"
									tabindex="0"
									role="radio"
									aria-checked=${selected === variant}
									aria-label=${this.t(`admin.ticketTemplates.variant.${variant}`)}
									class=${this.classMap({
										"flex-none overflow-hidden rounded-2xl cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2": true,
										"ring-2 ring-primary-default": selected === variant,
									})}
									@click=${() => this.select(variant)}
									@keydown=${(e: KeyboardEvent) => this.handleActivateKey(e, variant)}
								>
									<ticket-template-preview
											.variant=${variant}
											.width=${THUMB_WIDTH}
										></ticket-template-preview>
								</schmancy-surface>
							`,
						)}
					</div>
				</schmancy-scroll>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-template-picker": TicketTemplatePicker;
	}
}
